import {useMutation} from '@tanstack/react-query';
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {storeMutationAdapter} from '../../../../adapter/storeMutationAdapter';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {twMerge} from '../../../../common/twMerge';
import {TextTKey} from '../../../../common/type';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {TabPositionControl} from '../../../../design-system/atoms/TabPositionControl/TabPositionControl';
import {BelowDefaultTab} from '../../../../design-system/organisms/BelowDefaultTab/BelowDefaultTab';
import {CommentInputField} from '../../../../design-system/organisms/CommentInputField/CommentInputField';
import {CommentSpoilerButton} from '../../../../design-system/organisms/CommentSpoilerButton/CommentSpoilerButton';
import {EmoticonPreview} from '../../../../design-system/organisms/EmoticonPreview/EmoticonPreview';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {PcCommentInputField} from '../../../../design-system/organisms/PcCommentInputField';
import {PC_HEADER_HEIGHT} from '../../../../design-system/organisms/PcHeader/constants';
import {commonErrorHandler} from '../../../../errors/commonErrorHandler';
import {PagewebErrorType} from '../../../../errors/PagewebErrorType.enum';
import {
  Maybe,
  ResultStatus,
  SortOption,
} from '../../../../graphql/generated/types';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../header/constants';
import {setHeader} from '../../../../header/HeaderSlice';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {useLogin} from '../../../../user/useLogin.hook';
import {useInfiniteCommentListQuery} from '../../../../wrapper';
import {COMMENT_TAB_TEXT_MAP, CommentTabType} from '../../../comment/constant';
import {useCommentSortOptDialog} from '../../../comment/hook/useCommentSortOptDialog';
import {useUpdateCommentEmoticon} from '../../../comment/hook/useUpdateCommentEmoticon';
import {useWriteCommentBlockDialog} from '../../../comment/hook/useWriteCommentBlockDialog';
import {EmoticonScript} from '../../../comment/presentational/EmoticonScript';
import {EmoticonItem} from '../../../comment/type';
import {getCommentCooldownLocalStorage} from '../../../comment/util/getCommentCooldownLocalStorage';
import {setCommentCooldownLocalStorage} from '../../../comment/util/setCommentCooldownLocalStorage';
import {setSelectEmoticonCallback} from '../../../comment/util/setSelectEmoticonCallback';
import {CommentAllListContainer} from './CommentAllListContainer';
import {CommentBestListContainer} from './CommentBestListContainer';

export interface CommentTabContainerProps {
  seriesId: number;
  productId?: number;
  isContentHome?: boolean;
  initialSortType?: Maybe<string>;
  currentTab: CommentTabType;
  onChangeTab: (tab: CommentTabType) => void;
}

export function CommentTabContainer({
  seriesId,
  productId,
  isContentHome,
  initialSortType = null,
  onChangeTab,
  currentTab,
}: CommentTabContainerProps) {
  const onClickTab = useCallback(
    (id: string | number) => {
      onChangeTab(id as CommentTabType);
    },
    [onChangeTab]
  );

  const [commentInputText, setCommentInputText] = useState('');
  const [selectedEmoticon, setSelectedEmoticon] = useState<
    EmoticonItem | undefined
  >();
  const onChangeText = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentInputText(e.target.value);
  }, []);

  const {isLogin, doLogin} = useLogin();
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const checkLogin = useCallback(() => {
    if (isLogin) {
      return undefined;
    }
    doLogin();
  }, [doLogin, isLogin]);

  const {showBlockDialog, blockDialogComponent} = useWriteCommentBlockDialog();

  const [selectedSortOpt, setSelectedSortOpt] = useState<Maybe<SortOption>>();

  const {data: commentList, refetch} = useInfiniteCommentListQuery(
    {
      commentListInput: {
        page: 0,
        seriesId,
        productId,
        sortType: selectedSortOpt?.param || initialSortType,
      },
    },
    {
      initialPageParam: {
        commentListInput: {
          page: 0,
          seriesId,
          productId,
          sortType: selectedSortOpt?.param || initialSortType,
        },
      },
      getNextPageParam: () => undefined,
    }
  );

  const dispatch = useTypedDispatch();
  const commentCount = commentList?.pages[0].commentList?.totalCount;
  useEffect(() => {
    if (commentCount !== undefined && !isContentHome) {
      dispatch(
        setHeader({
          customTitle: t('comment_title', {
            values: {
              value: formatWithCommas(commentCount),
            },
          }),
        })
      );
    }
  }, [commentCount, dispatch, isContentHome]);

  useEffect(() => {
    if (!commentList || !commentList?.pages[0].commentList?.selectedSortOpt)
      return;
    setSelectedSortOpt(commentList?.pages[0].commentList?.selectedSortOpt);
  }, [commentList]);
  const {sortOptList, totalCount} = commentList?.pages[0].commentList || {};

  const userUid = useTypedSelector(state => state.user.user?.uid || '');

  const onSelectSortOption = useCallback(
    (sortOptIndex: number) => {
      if (!sortOptList) return;
      setSelectedSortOpt(sortOptList[sortOptIndex]);
    },
    [sortOptList]
  );

  const {showDialog, commentSortOptDialog} = useCommentSortOptDialog({
    isMobile,
    productId,
    sortOptList,
    selectedSortOpt,
    onSelectSortOption,
  });

  const [isSpoilerInputChecked, setIsSpoilerInputChecked] = useState(false);
  const onClickSpoiler = useCallback(() => {
    setIsSpoilerInputChecked(!isSpoilerInputChecked);
  }, [isSpoilerInputChecked]);

  const {mutateAsync: mutateAsyncWriteComment} = useMutation(
    storeMutationAdapter.writeComment()
  );

  const onSubmit = useCallback(async () => {
    if (commentInputText || selectedEmoticon) {
      const {uid, expires} = getCommentCooldownLocalStorage();
      if (uid === userUid && new Date(expires) > new Date()) {
        commonErrorHandler({
          isSerializedPagewebError: true,
          type: PagewebErrorType.CommentCooldownError,
          message: t('comment_toast_overload'),
        });
        return;
      }

      setCommentCooldownLocalStorage(userUid);

      const result = await mutateAsyncWriteComment({
        writeCommentInput: {
          seriesId,
          productId,
          isSpoiler: isSpoilerInputChecked,
          comment: selectedEmoticon
            ? `(이모티콘)${commentInputText}`
            : commentInputText,
          emoticon: selectedEmoticon
            ? JSON.stringify({
                item_id: selectedEmoticon.item_id,
                item_sub_type: selectedEmoticon.item_sub_type,
                item_ver: selectedEmoticon.item_version,
                resource_id: selectedEmoticon.resource_id,
              })
            : undefined,
        },
      });

      setCommentInputText('');
      setSelectedEmoticon(undefined);
      setIsSpoilerInputChecked(false);

      if (result.writeComment.status === ResultStatus.Success) {
        await refetch();
        onClickTab(CommentTabType.All);
      } else {
        showBlockDialog(result.writeComment.blockMsg);
      }
    }
  }, [
    commentInputText,
    selectedEmoticon,
    userUid,
    mutateAsyncWriteComment,
    seriesId,
    productId,
    isSpoilerInputChecked,
    refetch,
    showBlockDialog,
    onClickTab,
  ]);

  const user = useTypedSelector(state => state.user.user);

  useUpdateCommentEmoticon({commentList});

  const emoticonKeyboardWrapperRef = useRef<HTMLDivElement>(null);

  const onClickEmoticonIcon = useCallback((e: MouseEvent) => {
    LOG_CLICK_EVENT('이모티콘버튼_클릭', e, async () => {
      await setSelectEmoticonCallback(emoticon => {
        setSelectedEmoticon(emoticon);
      });
      window.Kakao.Emoticon.toggleKeyboard(emoticonKeyboardWrapperRef.current);
    });
  }, []);

  const clearEmoticon = useCallback(() => {
    setSelectedEmoticon(undefined);
  }, []);

  return (
    <div
      className="flex flex-col"
      style={{
        minHeight: `calc(${isMobile ? '100vh' : '852px'} - ${
          isMobile ? MOBILE_HEADER_HEIGHT_PX : PC_HEADER_HEIGHT
        }px)`,
      }}
      {...putTiaraObjectProps({
        customProps: {
          ...(productId ? {event_episode_id: String(productId)} : {}),
        },
      })}
    >
      <EmoticonScript />
      <TabPositionControl
        depth={1}
        isMobile={isMobile}
        isWebView={isWebView}
        disabled={!isMobile}
      >
        <MainTab
          isMobile={isMobile}
          title={t('comment_title', {
            values: {
              value: formatWithCommas(totalCount || 0),
            },
          })}
          tabItems={COMMENT_TAB_LIST}
          currentTabItemId={currentTab}
          onClickTab={onClickTab}
          showBorder={true}
          titleClassName={isContentHome ? 'font-small1-bold py-15pxr' : ''}
          selectBoxText={
            !isMobile && currentTab === CommentTabType.All && selectedSortOpt
              ? selectedSortOpt.name
              : undefined
          }
          onClickSelectBox={
            !isMobile && currentTab === CommentTabType.All
              ? showDialog
              : undefined
          }
          className={
            !isMobile
              ? isContentHome
                ? 'mb-24pxr mt-16pxr px-15pxr'
                : 'px-15pxr'
              : 'p-0'
          }
        />
      </TabPositionControl>
      {isMobile &&
        currentTab === CommentTabType.All &&
        sortOptList &&
        selectedSortOpt && (
          <TabPositionControl
            depth={2}
            isMobile={isMobile}
            isWebView={isWebView}
            disabled={!isMobile}
          >
            <BelowDefaultTab
              className={twMerge('px-18pxr', !isMobile && 'bg-transparent')}
            >
              <BelowDefaultTab.TextLabel className="font-small2-bold text-theme-solid-100">
                {t('common_all_count', {
                  values: {value: formatWithCommas(totalCount || 0)},
                })}
              </BelowDefaultTab.TextLabel>
              <BelowDefaultTab.SelectBox
                iconType={IconType.ContentHomeFilter}
                onClick={showDialog}
              >
                {selectedSortOpt.name}
              </BelowDefaultTab.SelectBox>
            </BelowDefaultTab>
          </TabPositionControl>
        )}

      {selectedEmoticon && (
        <EmoticonPreview
          imageUrl={selectedEmoticon.image_url}
          onClickClose={clearEmoticon}
        />
      )}
      <div className={isContentHome ? 'pb-16pxr' : ''} onClick={checkLogin}>
        {isMobile && (
          <>
            <CommentInputField
              value={commentInputText}
              onChange={onChangeText}
              onSubmit={user ? onSubmit : undefined}
              onClickSend={e => {
                LOG_CLICK_EVENT('전송버튼_클릭', e, () => {
                  if (user) {
                    onSubmit();
                  }
                });
              }}
              onClick={e => LOG_CLICK_EVENT('댓글입력필드_클릭', e)}
              readOnly={!user}
              onClickEmoticonIcon={onClickEmoticonIcon}
              isEmoticonSelected={!!selectedEmoticon}
              isMobile={isMobile}
            />
            <div className="pb-15pxr">
              <CommentSpoilerButton
                {...putTiaraObjectProps({
                  click: {
                    layer1: '스포일러',
                    copy: isSpoilerInputChecked ? 'OFF' : 'ON',
                  },
                })}
                isSpoiler={isSpoilerInputChecked}
                onClickSpoiler={e =>
                  LOG_CLICK_EVENT('스포일러옵션_클릭', e, onClickSpoiler)
                }
              />
            </div>
            <div
              ref={emoticonKeyboardWrapperRef}
              // inline style none을 넣어둬야 emoticon keyboard toggle 했을 때 바로 이모티콘 키보드가 열립니다.
              style={{display: 'none'}}
              className="mt-10pxr"
            />
          </>
        )}
        {!isMobile && !isContentHome && (
          <>
            <PcCommentInputField
              value={commentInputText}
              onChange={onChangeText}
              onClickSend={e => {
                LOG_CLICK_EVENT('전송버튼_클릭', e, () => {
                  if (user) {
                    onSubmit();
                  }
                });
              }}
              onClick={e => LOG_CLICK_EVENT('댓글입력필드_클릭', e)}
              readOnly={!user}
              isMobile={isMobile}
            />
            <div className="pb-15pxr pt-10pxr">
              <CommentSpoilerButton
                {...putTiaraObjectProps({
                  click: {
                    layer1: '스포일러',
                    copy: isSpoilerInputChecked ? 'OFF' : 'ON',
                  },
                })}
                isSpoiler={isSpoilerInputChecked}
                onClickSpoiler={e =>
                  LOG_CLICK_EVENT('스포일러옵션_클릭', e, onClickSpoiler)
                }
              />
            </div>
          </>
        )}
      </div>
      {currentTab === CommentTabType.Best ? (
        <CommentBestListContainer
          seriesId={seriesId}
          productId={productId}
          isContentHome={isContentHome}
        />
      ) : (
        <CommentAllListContainer
          seriesId={seriesId}
          productId={productId}
          isContentHome={isContentHome}
          initialSortType={initialSortType}
          selectedSortOpt={selectedSortOpt}
        />
      )}
      {blockDialogComponent}
      {commentSortOptDialog}
    </div>
  );
}

const COMMENT_TAB_LIST: Array<
  TextTKey & {
    id: CommentTabType;
  }
> = [
  {
    id: CommentTabType.Best,
    text: COMMENT_TAB_TEXT_MAP[CommentTabType.Best].text,
    textTKey: COMMENT_TAB_TEXT_MAP[CommentTabType.Best].textTKey,
  },
  {
    id: CommentTabType.All,
    text: COMMENT_TAB_TEXT_MAP[CommentTabType.All].text,
    textTKey: COMMENT_TAB_TEXT_MAP[CommentTabType.All].textTKey,
  },
];
